import React, { useMemo, useCallback, RefObject } from "react";
import type { FormInstance } from "antd";
import { Tag } from "../../../types/space";
import { createSearchButtons } from "../configs/searchConfig";
import {
  createTableColumns,
  createTablePanelButtons,
  TABLE_CONFIG,
} from "../configs/tableConfig";
import { createStatisticData } from "../configs/statisticConfig";

interface UseTagPageConfigParams {
  // 表单引用
  searchFormRef: RefObject<FormInstance>;
  formRef: RefObject<FormInstance>;
  // 列设置按钮状态
  columnSettingButton: React.ReactNode | null;
  // 统计数据
  statistics: {
    total: number;
    withIcon: number;
    withColor: number;
    withDescription: number;
  };
  // 业务逻辑处理函数
  handleSearch: (params: any) => void;
  loadTags: () => void;
  handleCreate: () => void;
  handleEdit: (tag: Tag) => void;
  handleDelete: (id: string) => void;
  handleFormSuccess: (values: any) => Promise<boolean>;
  closeFormModal: () => void;
}

export const useTagPageConfig = (params: UseTagPageConfigParams) => {
  const {
    searchFormRef,
    formRef,
    columnSettingButton,
    statistics,
    handleSearch,
    loadTags,
    handleCreate,
    handleEdit,
    handleDelete,
    handleFormSuccess,
    closeFormModal,
  } = params;

  // 统计数据配置 - 使用接口返回的统计数据
  const statisticData = useMemo(
    () => createStatisticData(statistics),
    [statistics]
  );

  // 处理搜索按钮点击
  const handleSearchClick = useCallback(() => {
    if (searchFormRef.current) {
      searchFormRef.current
        .validateFields()
        .then((values: any) => {
          handleSearch(values);
        })
        .catch(() => {
          // 验证失败时也执行搜索（使用当前表单值）
          const currentValues = searchFormRef.current?.getFieldsValue() || {};
          handleSearch(currentValues);
        });
    }
  }, [handleSearch, searchFormRef]);

  // 处理重置
  const handleReset = useCallback(() => {
    if (searchFormRef.current) {
      searchFormRef.current.resetFields();
    }
    handleSearch({});
  }, [handleSearch, searchFormRef]);

  // 创建搜索面板按钮配置
  const searchPanelButtons = useMemo(
    () =>
      createSearchButtons({
        handleSearchClick,
        handleReset,
      }),
    [handleSearchClick, handleReset]
  );

  // 创建表格按钮配置
  const tableButtons = useMemo(
    () =>
      createTablePanelButtons({
        loadTags,
        handleCreate,
      }),
    [loadTags, handleCreate]
  );

  // 创建表格列配置
  const columns = useMemo(
    () =>
      createTableColumns({
        handleEdit,
        handleDelete,
      }),
    [handleEdit, handleDelete]
  );

  // 表格标题（包含列设置按钮）
  const tableTitle = useMemo(() => {
    if (columnSettingButton) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{TABLE_CONFIG.TABLE_TITLE}</span>
          {columnSettingButton}
        </div>
      );
    }
    return TABLE_CONFIG.TABLE_TITLE;
  }, [columnSettingButton]);

  // 表单提交处理
  const handleFormSubmit = useCallback(async () => {
    try {
      const values = await formRef.current?.validateFields();
      const success = await handleFormSuccess(values);
      if (success) {
        closeFormModal();
      }
    } catch (error) {
      console.error("表单验证失败:", error);
    }
  }, [formRef, handleFormSuccess, closeFormModal]);

  return {
    statisticData,
    handleSearchClick,
    handleReset,
    searchPanelButtons,
    tableButtons,
    columns,
    tableTitle,
    handleFormSubmit,
  };
};

